import {
  vesperFetcher,
  vesperYieldKey,
} from '../../yields/intrinsic/fetchers/vesper'
import type { SavingsRegistryEntry } from './registry'

/**
 * Vesper `va*` pool rows (Ethereum / Base / Optimism — see VESPER.md at the
 * repo root for the full assessment). Strategy-backed single-asset pools:
 * deposits route through weighted strategies (Morpho / Aave / Compound /
 * Yearn lending legs, some leveraged borrow loops) and `pricePerShare()`
 * accrues what they earn net of the 0.5–2 % universal fee (charged on
 * YIELD, not principal — there is no deposit or withdraw fee on v5).
 *
 * Facts verified on-chain 2026-08-27 (probes on all three chains):
 *
 *  - **Not ERC-4626 in any part** — `asset()`, `totalAssets()`,
 *    `convertToAssets()` and every `max*` view revert; the surface is
 *    `token()` / `pricePerShare()` / `totalValue()`, hence the
 *    `vesper-pool` reader.
 *  - **Both legs are strictly `msg.sender`**: `deposit(uint256)` mints to
 *    the caller and `withdraw(uint256 shares)` burns the caller's shares
 *    and pays the caller — no receiver parameter, no owner/allowance path
 *    anywhere (probed: `withdraw` reverts `ERC20: burn amount exceeds
 *    balance` for a caller without shares). EOA-direct only, the wren
 *    shape — the actions layer refuses `receiver != operator` on both
 *    legs.
 *  - Exit is single-transaction `instant`: `withdraw` pays from the pool
 *    buffer and unwinds strategies for the remainder in the same tx. No
 *    queue, no cooldown, no cancel.
 *  - The ETH pools take **WETH** via `deposit(uint256)` (`token()` is
 *    WETH; a payable no-arg `deposit()` also exists on them but is not
 *    routed — one uniform ERC-20 leg beats two per-pool shapes).
 *
 * CURATION: v5 (`va*`) pools above ~$100k only. Deliberately excluded:
 * the legacy v2 generation (vUSDC / vETH / vWBTC — no `pricePerShare()`,
 * near-zero rates, run-off), the sub-$100k tail (vaRETH, vaCBETH, vaLINK,
 * vaFRAX, vaWBTC — two of which ran NEGATIVE realized rates), and the
 * dead Avalanche/Polygon deployments (API hosts retired).
 *
 * `solvency` is deliberately ABSENT on every row: the backing is on-chain
 * strategy positions in third-party lending markets, but the leveraged
 * borrow strategies can and do realize losses (vaCBETH / vaWBTC printed
 * negative 30-day rates with `interestEarned` negative on-chain), and no
 * loss-waterfall label in the union describes "strategy P&L flows to the
 * share price directly". Absent = not assessed, which is the honest
 * answer.
 *
 * The rate is the REALIZED trailing-30d figure from Vesper's own API and
 * can be negative — see [the fetcher](../../yields/intrinsic/fetchers/vesper.ts)
 * for why DefiLlama's forward-looking number is only the outage fallback.
 */
export const VESPER_ENTRIES: { [chainId: string]: SavingsRegistryEntry[] } = {
  '1': [
    {
      // vamsETH — the largest Vesper pool. NB **msETH IS DEPEGGED**:
      // CoinGecko and DefiLlama both price it ~0.675 of ETH (conf 0.99,
      // 2026-08-27) while Vesper's own API prices it AT PAR — so Vesper's
      // "$34.7M" headline is really ~$23M at market. Our price feed
      // carries the market price, so the row's USD columns are honest;
      // the DESCRIPTION is the only place the depeg itself can be said
      // (the wiTRY lira rule: `supplyRate` is asset-denominated and no
      // numeric field can flag a broken peg).
      reader: 'vesper-pool',
      address: '0xca7c607c590ad16007ccbbba9d26f4df656a36c2',
      underlying: '0x64351fc9810adad17a690e4e1717df5e7e085160', // msETH
      symbol: 'vamsETH',
      brand: 'Vesper',
      description:
        "Vesper's msETH Grow pool: msETH (Metronome's ETH-pegged synth, from Vesper's sister protocol) is lent through Morpho and StakeDAO strategies, and the share price accrues what they earn net of a 0.5% fee on yield. The rate is the realized trailing-30-day figure and is earned in msETH — which has traded MATERIALLY BELOW its ETH peg (about a third below at integration), so the position's ETH value depends on that discount as much as on the rate. Deposit and single-transaction withdrawal, both strictly for the calling wallet.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      yieldFetcher: vesperFetcher,
      yieldKey: vesperYieldKey(
        '1',
        '0xca7c607c590ad16007ccbbba9d26f4df656a36c2',
      ),
    },
    {
      // vamsUSD — the USD-synth sibling of vamsETH, and **msUSD IS
      // DEPEGGED harder in ranking terms**: ~$0.674 on CoinGecko and
      // DefiLlama (conf 0.99, 2026-08-27) against Vesper's own par
      // pricing — ~$21.5M at market, not "$31.9M". See the vamsETH note.
      reader: 'vesper-pool',
      address: '0x4c73f025a1947ec770327b9956fc61f535f72c22',
      underlying: '0xab5eb14c09d416f0ac63661e57edb7aecdb9befa', // msUSD
      symbol: 'vamsUSD',
      brand: 'Vesper',
      description:
        "Vesper's msUSD Grow pool: msUSD (Metronome's USD-pegged synth) is lent through Morpho and StakeDAO strategies, and the share price accrues what they earn net of a 0.5% fee on yield. The rate is the realized trailing-30-day figure and is earned in msUSD — which has traded MATERIALLY BELOW its dollar peg (around $0.67 at integration), so the position's dollar value depends on that discount as much as on the rate. Deposit and single-transaction withdrawal, both strictly for the calling wallet.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      yieldFetcher: vesperFetcher,
      yieldKey: vesperYieldKey(
        '1',
        '0x4c73f025a1947ec770327b9956fc61f535f72c22',
      ),
    },
    {
      // vaSTETH — ~$14.3M. NB the underlying stETH REBASES, and the rebase
      // accrues to the pool's balance, i.e. INTO pricePerShare — so the
      // pool's realized rate (~1.6 %) is the TOTAL stETH-denominated
      // return including the staking rebase, not a spread on top of it.
      // Holding raw stETH paid more when this was written; the row states
      // the basis so the comparison is at least possible.
      reader: 'vesper-pool',
      address: '0x4dbe3f01abe271d3e65432c74851625a8c30aa7b',
      underlying: '0xae7ab96520de3a18e5e111b5eaab095312d7fe84', // stETH
      symbol: 'vaSTETH',
      brand: 'Vesper',
      description:
        "Vesper's stETH Grow pool: stETH is deployed as collateral in Aave-based borrow strategies and the share price accrues the result. Because stETH rebases into the pool, the published realized rate is the pool's TOTAL stETH-denominated return — including the staking rebase, not on top of it — and has trailed plain stETH's own yield. Deposit and single-transaction withdrawal, both strictly for the calling wallet.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      yieldFetcher: vesperFetcher,
      yieldKey: vesperYieldKey(
        '1',
        '0x4dbe3f01abe271d3e65432c74851625a8c30aa7b',
      ),
    },
    {
      // vaETH — ~$4.2M. `token()` is WETH; the payable no-arg `deposit()`
      // exists but is not routed.
      reader: 'vesper-pool',
      address: '0xd1c117319b3595fbc39b471ab1fd485629eb05f2',
      underlying: '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2', // WETH
      symbol: 'vaETH',
      brand: 'Vesper',
      description:
        "Vesper's ETH Grow pool: WETH is routed through lending strategies (Morpho, Compound V3 and others) and the share price accrues what they earn net of a 1% fee on yield. The published rate is the realized trailing-30-day figure. Deposit (in WETH) and single-transaction withdrawal, both strictly for the calling wallet.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      yieldFetcher: vesperFetcher,
      yieldKey: vesperYieldKey(
        '1',
        '0xd1c117319b3595fbc39b471ab1fd485629eb05f2',
      ),
    },
    {
      // vaUSDC — ~$509k. 18-dec shares over 6-dec USDC (every v5 share is
      // 18-dec regardless of the underlying).
      reader: 'vesper-pool',
      address: '0xa8b607aa09b6a2e306f93e74c282fb13f6a80452',
      underlying: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48', // USDC
      symbol: 'vaUSDC',
      brand: 'Vesper',
      decimals: 18,
      underlyingDecimals: 6,
      description:
        "Vesper's USDC Grow pool: USDC is routed through Morpho and Convex lending strategies and the share price accrues what they earn net of a 2% fee on yield. The published rate is the realized trailing-30-day figure. Deposit and single-transaction withdrawal, both strictly for the calling wallet.",
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      yieldFetcher: vesperFetcher,
      yieldKey: vesperYieldKey(
        '1',
        '0xa8b607aa09b6a2e306f93e74c282fb13f6a80452',
      ),
    },
    {
      // vaDAI — ~$151k, a single Yearn V3 strategy.
      reader: 'vesper-pool',
      address: '0x0538c8bac84e95a9df8ac10aad17dbe81b9e36ee',
      underlying: '0x6b175474e89094c44da98b954eedeac495271d0f', // DAI
      symbol: 'vaDAI',
      brand: 'Vesper',
      description:
        "Vesper's DAI Grow pool: DAI is deposited into a Yearn V3 strategy and the share price accrues what it earns net of a 2% fee on yield. The published rate is the realized trailing-30-day figure. Deposit and single-transaction withdrawal, both strictly for the calling wallet.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      yieldFetcher: vesperFetcher,
      yieldKey: vesperYieldKey(
        '1',
        '0x0538c8bac84e95a9df8ac10aad17dbe81b9e36ee',
      ),
    },
  ],
  '8453': [
    {
      // vamsETH (Base) — 94 % of Vesper's Base book. Independent
      // deployment and independent msETH token from Ethereum's, but the
      // SAME depegged synth economics — and the price feed keys both
      // chains' msETH by one group, so the row prices at the market
      // discount either way. See the chain-1 vamsETH note.
      reader: 'vesper-pool',
      address: '0x913ece180df83a2b81a4976f83ca88543a0c51b8',
      underlying: '0x7ba6f01772924a82d9626c126347a28299e98c98', // msETH (Base)
      symbol: 'vamsETH',
      brand: 'Vesper',
      description:
        "Vesper's msETH Grow pool on Base: msETH (Metronome's ETH-pegged synth, a chain-local deployment) is lent through Morpho strategies and the share price accrues what they earn net of a 0.5% fee on yield. The rate is the realized trailing-30-day figure and is earned in msETH — which has traded MATERIALLY BELOW its ETH peg (about a third below at integration), so the position's ETH value depends on that discount as much as on the rate. Deposit and single-transaction withdrawal, both strictly for the calling wallet.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      yieldFetcher: vesperFetcher,
      yieldKey: vesperYieldKey(
        '8453',
        '0x913ece180df83a2b81a4976f83ca88543a0c51b8',
      ),
    },
    {
      // vaETH (Base) — ~$490k over Base WETH.
      reader: 'vesper-pool',
      address: '0x82562507429876486b60af4f32390ef0947b3d13',
      underlying: '0x4200000000000000000000000000000000000006', // WETH
      symbol: 'vaETH',
      brand: 'Vesper',
      description:
        "Vesper's ETH Grow pool on Base: WETH is routed through lending strategies and the share price accrues what they earn net of a 2% fee on yield. The published rate is the realized trailing-30-day figure. Deposit (in WETH) and single-transaction withdrawal, both strictly for the calling wallet.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      yieldFetcher: vesperFetcher,
      yieldKey: vesperYieldKey(
        '8453',
        '0x82562507429876486b60af4f32390ef0947b3d13',
      ),
    },
  ],
  '10': [
    {
      // vawstETH (Optimism) — ~$1.5M, the largest OP pool.
      reader: 'vesper-pool',
      address: '0xdd63ae655b388cd782681b7821be37fdb6d0e78d',
      underlying: '0x1f32b1c2345538c0c6f582fcb022739c4a194ebb', // wstETH
      symbol: 'vawstETH',
      brand: 'Vesper',
      description:
        "Vesper's wstETH Grow pool on Optimism: wstETH is deployed through Aave-based strategies and the share price accrues what they earn net of a 2% fee on yield, denominated in wstETH (on top of wstETH's own embedded staking yield). The published rate is the realized trailing-30-day figure. Deposit and single-transaction withdrawal, both strictly for the calling wallet.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      yieldFetcher: vesperFetcher,
      yieldKey: vesperYieldKey(
        '10',
        '0xdd63ae655b388cd782681b7821be37fdb6d0e78d',
      ),
    },
    {
      // vaUSDC (Optimism) — ~$938k. NB the underlying is BRIDGED USDC.e
      // (0x7f5c…), not native OP USDC.
      reader: 'vesper-pool',
      address: '0x539505dde2b9771debe0898a84441c5e7fdf6bc0',
      underlying: '0x7f5c764cbc14f9669b88837ca1490cca17c31607', // USDC.e
      symbol: 'vaUSDC',
      brand: 'Vesper',
      decimals: 18,
      underlyingDecimals: 6,
      description:
        "Vesper's USDC Grow pool on Optimism, over bridged USDC.e: deposits are routed through lending strategies and the share price accrues what they earn net of a 2% fee on yield. The published rate is the realized trailing-30-day figure. Deposit and single-transaction withdrawal, both strictly for the calling wallet.",
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      yieldFetcher: vesperFetcher,
      yieldKey: vesperYieldKey(
        '10',
        '0x539505dde2b9771debe0898a84441c5e7fdf6bc0',
      ),
    },
    {
      // vaETH (Optimism) — ~$380k over OP WETH.
      reader: 'vesper-pool',
      address: '0xccf3d1acf799bae67f6e354d685295557cf64761',
      underlying: '0x4200000000000000000000000000000000000006', // WETH
      symbol: 'vaETH',
      brand: 'Vesper',
      description:
        "Vesper's ETH Grow pool on Optimism: WETH is routed through lending strategies and the share price accrues what they earn net of a 2% fee on yield. The published rate is the realized trailing-30-day figure. Deposit (in WETH) and single-transaction withdrawal, both strictly for the calling wallet.",
      decimals: 18,
      isRebasing: false,
      isMintable: true,
      withdrawalMode: 'instant',
      yieldFetcher: vesperFetcher,
      yieldKey: vesperYieldKey(
        '10',
        '0xccf3d1acf799bae67f6e354d685295557cf64761',
      ),
    },
  ],
}
